/**
 * EVENTSFLO-PORTAL-I18N-PHASE-2A-FRAMEWORK-MIGRATION-V1
 *
 * react-i18next bootstrap.
 *
 * Design notes:
 *  - All bundled namespaces are loaded eagerly at startup. Total payload
 *    today is ~3 KB across en/fr/ru and 7 namespaces. Lazy / HTTP backend
 *    is deferred until bundle size becomes a concern.
 *  - localStorage key remains "eventsflo-language" (preserves user choice
 *    across the V1 → V2A migration).
 *  - English is the absolute fallback for any missing key in any
 *    namespace; missing keys log a warning in development only.
 *  - Reserved namespaces (terms, adminPortal, documents, emails) ship
 *    today as empty objects so the namespace list is stable.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// --- Namespace bundles ------------------------------------------------------
import commonEn from "./namespaces/common/en.json";
import commonFr from "./namespaces/common/fr.json";
import commonRu from "./namespaces/common/ru.json";

import termsEn from "./namespaces/terms/en.json";
import termsFr from "./namespaces/terms/fr.json";
import termsRu from "./namespaces/terms/ru.json";

import clientPortalEn from "./namespaces/clientPortal/en.json";
import clientPortalFr from "./namespaces/clientPortal/fr.json";
import clientPortalRu from "./namespaces/clientPortal/ru.json";

import crewPortalEn from "./namespaces/crewPortal/en.json";
import crewPortalFr from "./namespaces/crewPortal/fr.json";
import crewPortalRu from "./namespaces/crewPortal/ru.json";

import adminPortalEn from "./namespaces/adminPortal/en.json";
import adminPortalFr from "./namespaces/adminPortal/fr.json";
import adminPortalRu from "./namespaces/adminPortal/ru.json";

import documentsEn from "./namespaces/documents/en.json";
import documentsFr from "./namespaces/documents/fr.json";
import documentsRu from "./namespaces/documents/ru.json";

import emailsEn from "./namespaces/emails/en.json";
import emailsFr from "./namespaces/emails/fr.json";
import emailsRu from "./namespaces/emails/ru.json";

export const STORAGE_KEY = "eventsflo-language";
export const DEFAULT_LANGUAGE = "en";

// Single source of truth for the supported language registry. Per-portal
// filtering happens at the LanguageSelector via the `exclude` prop —
// Client Portal currently excludes `ru` (handled in ClientPortalPage).
export const LANGUAGE_OPTIONS = [
  { code: "en", labelKey: "common.english", nativeLabel: "English" },
  { code: "fr", labelKey: "common.french", nativeLabel: "Français" },
  { code: "ru", labelKey: "common.russian", nativeLabel: "Русский" },
];

export const SUPPORTED_LANGUAGES = LANGUAGE_OPTIONS.map((o) => o.code);

// Stable namespace ordering. `terms`, `adminPortal`, `documents`, `emails`
// are reserved (empty today) but registered so future translations land
// in the expected slot without code changes.
export const NAMESPACES = [
  "common",
  "terms",
  "clientPortal",
  "crewPortal",
  "adminPortal",
  "documents",
  "emails",
];

const RESOURCES = {
  en: {
    common: commonEn,
    terms: termsEn,
    clientPortal: clientPortalEn,
    crewPortal: crewPortalEn,
    adminPortal: adminPortalEn,
    documents: documentsEn,
    emails: emailsEn,
  },
  fr: {
    common: commonFr,
    terms: termsFr,
    clientPortal: clientPortalFr,
    crewPortal: crewPortalFr,
    adminPortal: adminPortalFr,
    documents: documentsFr,
    emails: emailsFr,
  },
  ru: {
    common: commonRu,
    terms: termsRu,
    clientPortal: clientPortalRu,
    crewPortal: crewPortalRu,
    adminPortal: adminPortalRu,
    documents: documentsRu,
    emails: emailsRu,
  },
};

function readStoredLanguage() {
  try {
    const stored = typeof window !== "undefined"
      ? window.localStorage.getItem(STORAGE_KEY)
      : null;
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch {
    /* ignore (SSR / privacy mode) */
  }
  return DEFAULT_LANGUAGE;
}

const IS_DEV =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV !== "production";

i18n.use(initReactI18next).init({
  resources: RESOURCES,
  lng: readStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: NAMESPACES,
  defaultNS: "common",
  // Keys are dot-notation paths inside each namespace JSON.
  keySeparator: ".",
  nsSeparator: ":",
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
  // Missing-key diagnostics: warn in dev only; never raise in prod.
  saveMissing: IS_DEV,
  missingKeyHandler: IS_DEV
    ? (lngs, ns, key) => {
        console.warn(
          `[i18n] missing key — lang=${(lngs && lngs[0]) || "?"} ns=${ns} key=${key}`,
        );
      }
    : undefined,
  react: {
    useSuspense: false, // resources are already in-memory; no async wait
  },
});

export default i18n;
